import type { MaybeRefOrGetter, Ref } from 'vue'
import { isRef, onMounted, onUnmounted, toValue, watch } from 'vue'

import type { HotkeyContextOptions, HotkeyHandler, HotkeyKey, HotkeyModifiers } from './hotkeys.types'
import { HotkeyService } from './hotkeyService'

export interface HotkeyBindingConfig {
  handler: HotkeyHandler
  description?: string
  allowInInputs?: boolean
  modifiers?: HotkeyModifiers
}

export type HotkeyBindingValue = HotkeyHandler | HotkeyBindingConfig

export type HotkeyBindings = Partial<Record<HotkeyKey, HotkeyBindingValue>>

export interface UseHotkeysOptions extends HotkeyContextOptions {
  /** Whether the context is enabled. Can be a boolean, ref, or getter function. Defaults to true. */
  enabled?: MaybeRefOrGetter<boolean>
}

function isBindingConfig(value: HotkeyBindingValue): value is HotkeyBindingConfig {
  return typeof value === 'object' && 'handler' in value
}

function isReactiveEnabled(enabled: MaybeRefOrGetter<boolean>): enabled is Ref<boolean> | (() => boolean) {
  return isRef(enabled) || typeof enabled === 'function'
}

export function useHotkeys(contextName: string, bindings: HotkeyBindings, options: UseHotkeysOptions = {}): void {
  const service = HotkeyService.getInstance()
  const { enabled = true, priority } = options

  onMounted(() => {
    service.init()

    const bindingsArray = Object.entries(bindings).map(([key, value]) => {
      if (isBindingConfig(value!)) {
        return {
          key: key as HotkeyKey,
          handler: value.handler,
          description: value.description,
          allowInInputs: value.allowInInputs,
          modifiers: value.modifiers
        }
      }
      return {
        key: key as HotkeyKey,
        handler: value as HotkeyHandler
      }
    })

    service.registerContext(contextName, bindingsArray, { priority })

    const initialEnabled = toValue(enabled)
    if (initialEnabled) {
      service.activateContext(contextName)
    }

    if (isReactiveEnabled(enabled)) {
      watch(
        () => toValue(enabled),
        newEnabled => {
          if (newEnabled) {
            service.activateContext(contextName)
          } else {
            service.deactivateContext(contextName)
          }
        }
      )
    }
  })

  onUnmounted(() => {
    service.removeContext(contextName)
  })
}
